import Quill from 'quill';
import '../rich-text-editor/qds-rich-text-editor.js';
import type { QdsRichTextEditor } from '../rich-text-editor/qds-rich-text-editor.js';
import { loadBaseCSS } from '../shared/button-utils.js';

loadBaseCSS();

const ALLOWED_FORMATS = ['bold', 'italic', 'underline', 'strike', 'link', 'blockquote', 'list'];

type QuillSelectionRange = { index: number; length: number };

type QuillIndexableBlot = {
  index(node: Node, offset: number): number;
};

type QuillSelectionState = {
  lastRange: QuillSelectionRange | null;
  savedRange: QuillSelectionRange;
};

type QuillShadowSelectionBridge = QuillSelectionState & {
  getNativeRange: () => unknown;
  normalizeNative: (range: Range) => unknown;
  setNativeRange: (startNode: Node | null, startOffset?: number, endNode?: Node | null, endOffset?: number, force?: boolean) => void;
  hasFocus: () => boolean;
  qdsShadowSelectionBridge?: boolean;
};

function sanitizeInitialHtml(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function normalizeEditorHtml(quill: Quill): string {
  const text = quill.getText().trim();
  if (!text) {
    return '';
  }

  return quill.root.innerHTML
    .replace(/\s*class="ql-align-[^"]*"/g, '')
    .trim();
}

/**
 * @element qds-inline-rich-text
 *
 * Quill-backed inline rich text editor for narrative content blocks rendered
 * directly on the content canvas. It keeps editing local and emits
 * `value-change` on blur/commit.
 *
 * @attr {string} value - Initial HTML value
 * @attr {string} property - Config field name emitted with `value-change`
 * @attr {string} placeholder - Placeholder shown for empty content
 * @attr {'full'} toolbar - Opt in to the shared full HTML editor; omitted keeps compact inline editing.
 * @attr {boolean} floating-toolbar - Place the full toolbar in the browser top layer.
 * @attr {boolean} readonly - Disables editing when present
 *
 * @fires value-change - Fired when the editor commits a changed HTML value
 */
export class QdsInlineRichText extends HTMLElement {
  private _property = '';
  private _value = '';
  private _draftValue = '';
  private _placeholder = '';
  private _readonly = false;
  private _focused = false;
  private _quill: Quill | null = null;
  private _fullEditor: QdsRichTextEditor | null = null;
  private _syncingFromValue = false;
  private _lastSelectionRange: QuillSelectionRange | null = null;

  static get observedAttributes(): string[] {
    return ['property', 'value', 'placeholder', 'readonly'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (this.getAttribute('toolbar') === 'full') { this._mountFullEditor(); return; }
    this.render();
    this._bindEvents();
    this._ensureQuill();
  }

  disconnectedCallback(): void {
    this._fullEditor?.remove(); this._fullEditor = null;
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.removeEventListener('pointerdown', this._handleMouseDown as EventListener);
    this.shadowRoot.removeEventListener('pointerup', this._handleSelectionChange as EventListener);
    this.shadowRoot.removeEventListener('keyup', this._handleSelectionChange as EventListener);
    this.shadowRoot.removeEventListener('click', this._handleClick as EventListener);
    this.shadowRoot.removeEventListener('focusin', this._handleFocusIn as EventListener);
    this.shadowRoot.removeEventListener('focusout', this._handleFocusOut as EventListener);
  }

  attributeChangedCallback(name: string, _oldValue: string | null, newValue: string | null): void {
    switch (name) {
      case 'property':
        this._property = newValue || '';
        break;
      case 'value':
        this._value = sanitizeInitialHtml(newValue || '');
        this._draftValue = this._value;
        this._syncFromValue();
        break;
      case 'placeholder':
        this._placeholder = newValue || '';
        if (this._quill) {
          this._quill.root.dataset.placeholder = this._placeholder;
        }
        break;
      case 'readonly':
        this._readonly = newValue !== null;
        this._syncReadonly();
        break;
      default:
        break;
    }
  }

  set property(value: string | undefined) {
    this._property = value || '';
    if (value) {
      this.setAttribute('property', value);
    } else {
      this.removeAttribute('property');
    }
  }

  get property(): string {
    return this._property;
  }

  set value(value: unknown) {
    this._value = sanitizeInitialHtml(value);
    this._draftValue = this._value;
    this.setAttribute('value', this._value);
    this._syncFromValue();
  }

  get value(): string {
    return this._value;
  }

  set placeholder(value: string) {
    this._placeholder = value || '';
    if (value) {
      this.setAttribute('placeholder', value);
    } else {
      this.removeAttribute('placeholder');
    }
  }

  get placeholder(): string {
    return this._placeholder;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  get readonly(): boolean {
    return this._readonly;
  }

  private _mountFullEditor(): void {
    if (!this.shadowRoot || this._fullEditor) return;
    this.shadowRoot.innerHTML = `<style>
      :host { display: block; min-width: 0; color: inherit; font: inherit; }
      ::slotted(qds-rich-text-editor) { display: block; width: 100%; }
    </style><slot name="full-editor"></slot>`;
    const editor = document.createElement('qds-rich-text-editor') as QdsRichTextEditor;
    editor.slot = 'full-editor';
    editor.setAttribute('toolbar', 'full'); editor.setAttribute('portable-html', '');
    editor.toggleAttribute('floating-toolbar', this.hasAttribute('floating-toolbar'));
    editor.setAttribute('data-frame-ignore-select', 'true');
    editor.setAttribute('placeholder', this._placeholder);
    editor.value = this._value; editor.readonly = this._readonly;
    // Picker events must never be mistaken for a block's HTML value-change.
    editor.addEventListener('value-change', event => event.stopPropagation());
    editor.addEventListener('input', event => {
      if (event instanceof CustomEvent && event.detail?.component === editor) this._draftValue = editor.value;
    });
    editor.addEventListener('change', event => {
      if (!(event instanceof CustomEvent) || event.detail?.component !== editor || editor.value === this._value) return;
      event.stopPropagation();
      this._value = editor.value; this._draftValue = this._value;
      this.dispatchEvent(new CustomEvent('value-change', {
        detail: { property: this._property || undefined, value: this._value }, bubbles: true, composed: true
      }));
    });
    this._fullEditor = editor;
    this.appendChild(editor);
  }

  private _bindEvents(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.addEventListener('pointerdown', this._handleMouseDown as EventListener);
    this.shadowRoot.addEventListener('pointerup', this._handleSelectionChange as EventListener);
    this.shadowRoot.addEventListener('keyup', this._handleSelectionChange as EventListener);
    this.shadowRoot.addEventListener('click', this._handleClick as EventListener);
    this.shadowRoot.addEventListener('focusin', this._handleFocusIn as EventListener);
    this.shadowRoot.addEventListener('focusout', this._handleFocusOut as EventListener);
  }

  private _getToolbar(): HTMLElement | null {
    return this.shadowRoot?.querySelector<HTMLElement>('.inline-rich-text__toolbar') || null;
  }

  private _getEditorMount(): HTMLElement | null {
    return this.shadowRoot?.querySelector<HTMLElement>('.inline-rich-text__editor') || null;
  }

  private _ensureQuill(): void {
    if (this._quill || !this.shadowRoot) {
      return;
    }

    const editorMount = this._getEditorMount();
    const toolbar = this._getToolbar();
    if (!editorMount || !toolbar) {
      return;
    }

    this._quill = new Quill(editorMount, {
      bounds: editorMount,
      modules: {
        toolbar
      },
      placeholder: this._placeholder,
      readOnly: this._readonly,
      theme: null as unknown as string | undefined,
      formats: ALLOWED_FORMATS
    });
    this._bridgeQuillSelectionToShadowRoot();

    this._quill.root.setAttribute('data-frame-ignore-select', 'true');
    this._quill.root.setAttribute('spellcheck', 'true');
    this._quill.on('text-change', () => {
      if (this._syncingFromValue) {
        return;
      }

      this._draftValue = normalizeEditorHtml(this._quill!);
    });

    this._syncFromValue();
    this._syncReadonly();
  }

  private _syncFromValue(): void {
    if (this._fullEditor) { this._fullEditor.value = this._value; return; }
    if (!this._quill) {
      return;
    }

    const nextValue = this._value || '';
    const currentValue = normalizeEditorHtml(this._quill);
    if (currentValue === nextValue) {
      this._draftValue = nextValue;
      return;
    }

    this._syncingFromValue = true;
    if (nextValue) {
      this._quill.clipboard.dangerouslyPasteHTML(nextValue, 'silent');
    } else {
      this._quill.setText('', 'silent');
    }
    this._syncingFromValue = false;
    this._draftValue = nextValue;
  }

  private _syncReadonly(): void {
    if (this._fullEditor) this._fullEditor.readonly = this._readonly;
    this._quill?.enable(!this._readonly);
    this.toggleAttribute('data-focused', this._focused && !this._readonly);
  }

  private _resolveShadowSelectionRange(): QuillSelectionRange | null {
    if (!this._quill || !this.shadowRoot) {
      return null;
    }

    const shadowSelection = (this.shadowRoot as ShadowRoot & { getSelection?: () => Selection | null } | null)?.getSelection?.() || null;
    const selection = shadowSelection || document.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }

    const nativeRange = selection.getRangeAt(0);
    if (!this._quill.root.contains(nativeRange.startContainer) || !this._quill.root.contains(nativeRange.endContainer)) {
      return null;
    }

    const resolvePoint = (node: Node, offset: number): number | null => {
      if (node.nodeType === Node.TEXT_NODE) {
        const quill = this._quill;
        if (!quill) {
          return null;
        }

        const blot = (quill.constructor as typeof Quill).find(node, true) as QuillIndexableBlot | null;
        if (!blot) {
          return null;
        }

        return quill.getIndex(blot as any) + blot.index(node, offset);
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const childAtOffset = element.childNodes[offset] || null;
        if (childAtOffset) {
          return resolvePoint(childAtOffset, 0);
        }

        const previousChild = element.childNodes[offset - 1] || null;
        if (previousChild?.nodeType === Node.TEXT_NODE) {
          return resolvePoint(previousChild, previousChild.textContent?.length || 0);
        }
      }

      return null;
    };

    const start = resolvePoint(nativeRange.startContainer, nativeRange.startOffset);
    const end = resolvePoint(nativeRange.endContainer, nativeRange.endOffset);
    if (start === null || end === null) {
      return null;
    }

    return {
      index: Math.min(start, end),
      length: Math.max(0, Math.abs(end - start))
    };
  }

  private _syncSelectionFromShadowRoot(): QuillSelectionRange | null {
    if (!this._quill) {
      return null;
    }

    const range = this._recordShadowSelectionRange();
    if (!range) {
      return this._lastSelectionRange;
    }

    this._quill.setSelection(range.index, range.length, 'silent');
    return range;
  }

  /**
   * Remember a Shadow DOM selection without writing it back to the document.
   * Reapplying a stale native range on every keyup makes the visible caret
   * appear to remain before newly typed text.
   */
  private _recordShadowSelectionRange(): QuillSelectionRange | null {
    const range = this._resolveShadowSelectionRange();
    if (!range || !this._quill) {
      return null;
    }

    this._lastSelectionRange = range;
    const selection = (this._quill as Quill & { selection?: QuillSelectionState }).selection;
    if (selection) {
      selection.lastRange = range;
      selection.savedRange = range;
    }
    return range;
  }

  private _bridgeQuillSelectionToShadowRoot(): void {
    if (!this._quill || !this.shadowRoot) {
      return;
    }

    const quillSelection = (this._quill as Quill & { selection?: QuillShadowSelectionBridge }).selection;
    if (!quillSelection || quillSelection.qdsShadowSelectionBridge) {
      return;
    }

    const getSelection = (): Selection | null => {
      const shadowSelection = (this.shadowRoot as ShadowRoot & { getSelection?: () => Selection | null }).getSelection?.();
      return shadowSelection?.rangeCount ? shadowSelection : document.getSelection();
    };

    quillSelection.getNativeRange = () => {
      const selection = getSelection();
      return selection?.rangeCount ? quillSelection.normalizeNative(selection.getRangeAt(0)) : null;
    };
    quillSelection.hasFocus = () => this.shadowRoot?.activeElement === this._quill?.root;
    quillSelection.setNativeRange = (startNode, startOffset = 0, endNode = startNode, endOffset = startOffset) => {
      const selection = getSelection();
      if (!selection || !this._quill) {
        return;
      }
      if (!startNode || !endNode) {
        selection.removeAllRanges();
        this._quill.root.blur();
        return;
      }
      if (!quillSelection.hasFocus()) {
        this._quill.root.focus({ preventScroll: true });
      }
      const range = document.createRange();
      range.setStart(startNode, startOffset);
      range.setEnd(endNode, endOffset);
      selection.removeAllRanges();
      selection.addRange(range);
    };
    quillSelection.qdsShadowSelectionBridge = true;
  }

  private _emitCommit(): void {
    if (!this._quill) {
      return;
    }

    const nextValue = normalizeEditorHtml(this._quill);
    if (nextValue === this._value) {
      this._draftValue = nextValue;
      return;
    }

    this._value = nextValue;
    this._draftValue = nextValue;
    this.dispatchEvent(new CustomEvent('value-change', {
      detail: {
        property: this._property || undefined,
        value: nextValue
      },
      bubbles: true,
      composed: true
    }));
  }

  private _toggleFormat(format: string, value: unknown = true): void {
    if (!this._quill || this._readonly) {
      return;
    }

    const range = this._syncSelectionFromShadowRoot() || this._quill.getSelection() || this._lastSelectionRange;
    if (!range) {
      return;
    }

    const currentFormats = this._quill.getFormat(range);
    const isActive = Boolean(currentFormats[format]);
    const nextValue = isActive ? false : value;

    if (range.length > 0) {
      this._quill.formatText(range.index, range.length, format, nextValue, 'user');
      this._quill.setSelection(range.index, range.length, 'silent');
    } else {
      this._quill.setSelection(range.index, 0, 'silent');
      this._quill.format(format, nextValue, 'user');
    }

    this._lastSelectionRange = range;
    this._quill.focus();
  }

  private _handleMouseDown = (event: PointerEvent): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.inline-rich-text__button')) {
      this._recordShadowSelectionRange();
      event.preventDefault();
    }
  };

  private _handleSelectionChange = (): void => {
    if (this._readonly) {
      return;
    }

    this._recordShadowSelectionRange();
  };

  private _handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    const button = target?.closest<HTMLElement>('.inline-rich-text__button');
    if (!button) {
      return;
    }

    const format = button.dataset.format || '';
    if (!format) {
      return;
    }

    if (format === 'link') {
      if (!this._quill || this._readonly) {
        return;
      }

      const range = this._syncSelectionFromShadowRoot() || this._quill.getSelection() || this._lastSelectionRange;
      if (!range) {
        return;
      }

      this._quill.setSelection(range.index, range.length, 'silent');
      const url = window.prompt('Enter link URL');
      if (url) {
        if (range.length > 0) {
          this._quill.formatText(range.index, range.length, 'link', url, 'user');
        } else {
          this._quill.format('link', url, 'user');
        }
      }
      this._lastSelectionRange = range;
      this._quill.focus();
      return;
    }

    if (format === 'bullet-list') {
      this._toggleFormat('list', 'bullet');
      return;
    }

    if (format === 'ordered-list') {
      this._toggleFormat('list', 'ordered');
      return;
    }

    this._toggleFormat(format);
  };

  private _handleFocusIn = (): void => {
    if (this._readonly) {
      return;
    }

    this._focused = true;
    this.toggleAttribute('data-focused', true);
    this._syncSelectionFromShadowRoot();
  };

  private _handleFocusOut = (event: FocusEvent): void => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && this.shadowRoot?.contains(nextTarget)) {
      return;
    }

    this._focused = false;
    this.toggleAttribute('data-focused', false);
    this._lastSelectionRange = this._quill?.getSelection() || this._lastSelectionRange;
    this._emitCommit();
  };

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: relative;
          min-width: 0;
          color: inherit;
          font: inherit;
        }

        .inline-rich-text {
          position: relative;
          display: block;
          min-width: 0;
        }

        .inline-rich-text__toolbar {
          position: absolute;
          top: -44px;
          right: 0;
          z-index: 2;
          display: none;
          align-items: center;
          gap: 6px;
          padding: 6px;
          border: 1px solid var(--theme-border-primary, #dfe1e5);
          border-radius: 8px;
          background: var(--theme-background-primary, #ffffff);
          box-shadow: var(--shadow-dropdown, 0 4px 12px rgba(0, 0, 0, 0.15));
          touch-action: manipulation;
        }

        :host([data-focused]) .inline-rich-text__toolbar {
          display: flex;
        }

        .inline-rich-text__button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 32px;
          height: 32px;
          padding: 0 8px;
          border: 1px solid var(--theme-border-primary, #dfe1e5);
          border-radius: 4px;
          background: var(--theme-background-primary, #ffffff);
          color: var(--theme-text-primary, #333333);
          font: 600 13px/1 var(--font-family-primary, 'IBM Plex Sans', sans-serif);
          cursor: ${this._readonly ? 'default' : 'pointer'};
        }

        .inline-rich-text__button[disabled] {
          opacity: 0.55;
          cursor: default;
        }

        @media (max-width: 1200px) {
          .inline-rich-text__toolbar {
            top: -58px;
          }

          .inline-rich-text__button {
            min-width: 44px;
            height: 44px;
          }
        }

        .inline-rich-text__editor {
          min-height: 72px;
          padding: 8px 10px;
          background: transparent;
        }

        .inline-rich-text__editor .ql-editor {
          min-height: 56px;
          padding: 0;
          border: 0;
          outline: none;
          color: inherit;
          font: inherit;
          line-height: 1.6;
          white-space: normal;
        }

        .inline-rich-text__editor .ql-editor.ql-blank::before {
          left: 0;
          right: 0;
          color: var(--theme-text-tertiary, #80868b);
          font-style: normal;
        }

        /* Quill adds ql-container to this mount element, rather than a child. */
        .inline-rich-text__editor.ql-container {
          /* Override the third-party Quill theme if it is present. */
          border: 0 !important;
          outline: 0;
          box-shadow: none;
          background: transparent;
          font: inherit;
        }

        .inline-rich-text__editor .ql-editor p,
        .inline-rich-text__editor .ql-editor ul,
        .inline-rich-text__editor .ql-editor ol,
        .inline-rich-text__editor .ql-editor blockquote {
          margin: 0 0 0.8em;
        }

        /* Quill represents both list variants as <ol>. With no Quill theme,
           render the data-list marker explicitly so bullets do not inherit
           the browser's numbered-list marker. */
        .inline-rich-text__editor .ql-editor ol {
          padding-left: 1.5em;
          counter-reset: qds-inline-list;
        }

        .inline-rich-text__editor .ql-editor li {
          position: relative;
          padding-left: 1.5em;
          list-style: none;
        }

        .inline-rich-text__editor .ql-editor li > .ql-ui::before {
          display: inline-block;
          width: 1.2em;
          margin-left: -1.5em;
          margin-right: 0.3em;
          text-align: right;
          white-space: nowrap;
        }

        .inline-rich-text__editor .ql-editor li[data-list="bullet"] > .ql-ui::before {
          content: '•';
        }

        .inline-rich-text__editor .ql-editor li[data-list="ordered"] {
          counter-increment: qds-inline-list;
        }

        .inline-rich-text__editor .ql-editor li[data-list="ordered"] > .ql-ui::before {
          content: counter(qds-inline-list, decimal) '. ';
        }

        .inline-rich-text__editor .ql-editor p:last-child,
        .inline-rich-text__editor .ql-editor ul:last-child,
        .inline-rich-text__editor .ql-editor ol:last-child,
        .inline-rich-text__editor .ql-editor blockquote:last-child {
          margin-bottom: 0;
        }

        .inline-rich-text__editor .ql-editor blockquote {
          margin-left: 0;
          padding-left: 12px;
          border-left: 3px solid var(--theme-border-strong, #c5c9ce);
          color: var(--theme-text-secondary, #5f6368);
        }
      </style>
      <div class="inline-rich-text">
        <div class="inline-rich-text__toolbar" data-frame-ignore-select="true">
          <button type="button" class="inline-rich-text__button" data-format="bold" ${this._readonly ? 'disabled' : ''}><strong>B</strong></button>
          <button type="button" class="inline-rich-text__button" data-format="italic" ${this._readonly ? 'disabled' : ''}><em>I</em></button>
          <button type="button" class="inline-rich-text__button" data-format="underline" ${this._readonly ? 'disabled' : ''}><u>U</u></button>
          <button type="button" class="inline-rich-text__button" data-format="strike" ${this._readonly ? 'disabled' : ''}><s>S</s></button>
          <button type="button" class="inline-rich-text__button" data-format="link" ${this._readonly ? 'disabled' : ''}>Link</button>
          <button type="button" class="inline-rich-text__button" data-format="bullet-list" ${this._readonly ? 'disabled' : ''}>• List</button>
          <button type="button" class="inline-rich-text__button" data-format="ordered-list" ${this._readonly ? 'disabled' : ''}>1. List</button>
          <button type="button" class="inline-rich-text__button" data-format="blockquote" ${this._readonly ? 'disabled' : ''}>Quote</button>
        </div>
        <div class="inline-rich-text__editor" data-frame-ignore-select="true"></div>
      </div>
    `;
  }
}

if (!customElements.get('qds-inline-rich-text')) {
  customElements.define('qds-inline-rich-text', QdsInlineRichText);
}

export default QdsInlineRichText;
